import React from "react";
import Box from "@mui/material/Box";
import { keyframes, SxProps, Theme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { ParticipationEventState } from "@Interfaces";
import {
    PARTICIPATION_CONTROL_HEIGHT,
    PARTICIPATION_TEXT_MD,
} from "./ParticipationWidgets/participationStyles";

interface ParticipationHeaderProps {
    state: ParticipationEventState;
}

const pulse = keyframes`
    0% { opacity: 1; }
    50% { opacity: 0.35; }
    100% { opacity: 1; }
`;

const dotBaseSx = {
    width: 9,
    height: 9,
    borderRadius: "999px",
    boxSizing: "border-box",
    flexShrink: 0,
} as const;

const STATE_DISPLAY: Record<
    ParticipationEventState,
    { labelKey: string; dotSx: SxProps<Theme> }
> = {
    waiting: {
        labelKey: "pages.participation.header.waiting",
        dotSx: { ...dotBaseSx, border: "2px solid #AEB5DD" },
    },
    collecting: {
        labelKey: "pages.participation.header.collecting",
        dotSx: {
            ...dotBaseSx,
            backgroundColor: "#5EE0A0",
            animation: `${pulse} 2s ease-in-out infinite`,
        },
    },
    closed: {
        labelKey: "pages.participation.header.closed",
        dotSx: { ...dotBaseSx, backgroundColor: "#AEB5DD" },
    },
};

export const ParticipationHeader: React.FC<ParticipationHeaderProps> = ({
    state,
}) => {
    const { t } = useTranslation();
    // Unknown values fall back to "waiting" so a live event is never shown as closed.
    const { labelKey, dotSx } = STATE_DISPLAY[state] ?? STATE_DISPLAY.waiting;

    return (
        <Box
            role="status"
            sx={{
                background: "linear-gradient(90deg, #252E57 0%, #4C58A8 100%)",
                borderRadius: "10px",
                padding: "12px 16px 12px 20px",
                minHeight: PARTICIPATION_CONTROL_HEIGHT,
                display: "flex",
                alignItems: "center",
                gap: "10px",
            }}
        >
            <Box component="span" sx={dotSx} aria-hidden="true" />
            <Box
                component="span"
                sx={{
                    fontFamily:
                        '"Inter Display", Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                    fontSize: PARTICIPATION_TEXT_MD,
                    fontWeight: 600,
                    color: "#FFFFFF",
                }}
            >
                {t(labelKey)}
            </Box>
        </Box>
    );
};
